import { parseConceptMappings } from '@verantos-dev/feature-epi-chat';
import type {
  ConceptMapping,
  EpiAppliedKnowledge,
  EpiArtifact,
  EpiArtifactExecution,
  EpiArtifactOutputType,
  EpiArtifactOutputValueType,
  EpiArtifactStatus,
  EpiAttritionStep,
  EpiClinicalExplanation,
  EpiConceptBinding,
  EpiDesignDecision,
  EpiExecutionConceptUsage,
  EpiExecutionCoverage,
  EpiExecutionDistribution,
  EpiExecutionTechnicalChecks,
} from '@verantos-dev/feature-epi-chat';

import type { VariableDetail } from '../api/useVariableDetail';

/**
 * Synthesize a tabbed-surface ``EpiArtifact`` from a SQL-first AV
 * ``VariableDetail`` so the artifact card renders the same shell
 * across page / modal / drawer surfaces without the consumers
 * branching on backing store.
 *
 * Two backings funnel through this adapter:
 *   - knowledge rows (``av.<slug>``-keyed; agent-authored mid-session
 *     drafts). The knowledge detail endpoint enriches the wire payload
 *     with the manifest (S3 pre-save, library row post-save), so the
 *     adapter reads the same shape regardless.
 *   - library ``av_denovo`` rows (UUID-keyed). The library detail
 *     endpoint surfaces ``METADATA.manifest`` under ``detail.manifest``
 *     for the same reason.
 *
 * Populated by this adapter from the top-level ``detail`` fields:
 *   - ``content`` (SQL tab)
 *   - ``description`` / ``slug`` (the id, from ``av.<slug>``)
 *   - ``format`` (the output kind)
 *   - ``conceptGroupIds`` / ``sources``
 *   - ``artifactStatus`` (from ``assetStatus`` when present, else the
 *     manifest/column status)
 *
 * The display label (``dataElement``) comes from the manifest, NOT the REST
 * ``name`` (which is the stable id) — see the resolution below.
 *
 * Populated by this adapter from ``detail.manifest`` (the persisted
 * ``ArtifactManifest`` JSON the backend writes to S3 pre-save and to
 * ``ANALYTICAL_VARIABLE.METADATA.manifest`` post-save):
 *   - ``clinicalExplanation`` — Description tab body
 *   - ``conceptBindings`` — Concepts tab body
 *   - ``designDecisions`` — Decisions tab body
 *   - ``knowledgeApplied`` — Recall tab body + citation chip
 *   - ``coreLabel`` — header meta line
 *   - ``validationBlocking`` — header badge color, Verification gate
 *   - ``outputValues`` — Description tab's Values field (OR-6266)
 *
 * Manifest field names are snake_case (the Python backend's
 * ``model_dump`` shape); we convert to camelCase to match
 * ``EpiArtifact`` and friends. Defensive parsing throughout — the
 * ``manifest`` field is typed ``Record<string, unknown>`` because the
 * wire-format keys can drift; bad shapes fall through to ``undefined``
 * and the tab renders its empty state.
 */
export const sqlAvDetailToEpiArtifact = (detail: VariableDetail): EpiArtifact => {
  const slug = detail.analyticalVariableId.startsWith('av.')
    ? detail.analyticalVariableId.slice('av.'.length)
    : detail.name;
  const manifest = isRecord(detail.manifest) ? detail.manifest : null;
  const validation = isRecord(manifest?.['validation']) ? manifest!['validation'] : null;
  // Status from the manifest's lifecycle (``validated`` / ``blocked`` /
  // ``drafting``) is preferred over ``detail.status`` (which on the
  // library path is the column STATUS — ``curated`` / null). Knowledge
  // path keeps the row's persisted lifecycle value via the merge rule
  // in the backend enrichment.
  const manifestStatus = isString(manifest?.['status']) ? manifest!['status'] : null;
  // Display label (the card title). The library/knowledge REST ``name`` is the
  // stable id (the ``ANALYTICAL_VARIABLE.NAME`` column), so reading it as the
  // title showed the id where the label belongs (OR-6122/6153). Resolve the
  // human label from the manifest instead: new shape ``data_element``, old
  // shape ``name`` (display), falling back to the id only if neither is present.
  // ``manifest.name`` is the display label only in the OLD manifest shape
  // (identified by a ``slug`` key); in the NEW shape ``name`` is the id, so
  // never borrow it for the display slot there (mirrors the backend
  // ``manifest_identity`` guard). Last resort is the REST ``name`` (the id) —
  // only reached when the manifest carries no display label at all.
  const manifestIsOldShape = manifest != null && 'slug' in manifest;
  const dataElement =
    asOptionalString(manifest?.['data_element']) ??
    (manifestIsOldShape ? asOptionalString(manifest?.['name']) : undefined) ??
    detail.name;
  return {
    id: detail.analyticalVariableId,
    artifactType: 'sql',
    dataElement,
    content: detail.sqlBody ?? '',
    status: 'complete',
    updatedAt: detail.modifiedBy?.transactionDate ?? detail.createdBy?.transactionDate ?? '',
    name: slug, // the stable id (the slug value)
    kind: 'av',
    description: detail.description ?? undefined,
    outputValueType: knownOutputValueType(manifest?.['output_value_type']),
    outputValues: asOptionalString(manifest?.['output_values']),
    format: knownOutputType(detail.outputType),
    conceptGroupIds: detail.conceptGroupIds,
    sources: detail.sources,
    artifactStatus: toArtifactStatus(manifestStatus ?? detail.status, detail.assetStatus),
    coreLabel: isString(manifest?.['core_label']) ? manifest!['core_label'] : undefined,
    clinicalExplanation: extractClinicalExplanation(manifest?.['clinical_explanation']),
    conceptBindings: extractConceptBindings(manifest?.['concept_bindings']),
    conceptMappings: extractConceptMappings(manifest?.['concept_mappings']),
    designDecisions: extractDesignDecisions(manifest?.['design_decisions']),
    knowledgeApplied: extractKnowledgeApplied(manifest?.['knowledge_applied']),
    validationBlocking: validation ? Boolean(validation['blocking']) : undefined,
    executions: extractVerificationResults(manifest?.['verification_results']),
  };
};

const knownOutputType = (raw: string | null): EpiArtifactOutputType | undefined => {
  switch (raw) {
    case 'boolean':
    case 'numeric':
    case 'string':
    case 'date':
      return raw;
    default:
      return undefined;
  }
};

const knownOutputValueType = (raw: unknown): EpiArtifactOutputValueType | undefined => {
  switch (raw) {
    case 'boolean':
    case 'signed_integer':
    case 'unsigned_integer':
    case 'unsigned_decimal':
    case 'signed_decimal':
    case 'string':
    case 'date':
      return raw;
    default:
      return undefined;
  }
};

/**
 * Maps the unified asset-status vocabulary (``Draft`` / ``Processing`` /
 * ``Ready`` / ``Error`` — see ``shared_types.artifact_status.AssetStatus``)
 * to the chat-stream ``EpiArtifactStatus`` enum. ``Processing`` reads as
 * ``drafting`` — the AV isn't a finished, usable artifact yet either way.
 */
const assetStatusToArtifactStatus = (
  raw: string | null | undefined
): EpiArtifactStatus | undefined => {
  switch (raw) {
    case 'Draft':
    case 'Processing':
      return 'drafting';
    case 'Ready':
      return 'validated';
    case 'Error':
      return 'blocked';
    default:
      return undefined;
  }
};

const toArtifactStatus = (
  raw: string | null,
  assetStatus?: string | null
): EpiArtifactStatus | undefined => {
  // ``assetStatus`` (the unified REST vocabulary) takes precedence when
  // present. Otherwise fall back to the manifest-internal lifecycle
  // values; column STATUS values (``curated`` / null) fall through to
  // ``undefined`` and the card surface relies on the source-aware chip
  // resolver instead.
  const fromAssetStatus = assetStatusToArtifactStatus(assetStatus);
  if (fromAssetStatus) return fromAssetStatus;
  if (raw === 'validated' || raw === 'blocked' || raw === 'drafting') return raw;
  return undefined;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isString = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

const asOptionalString = (value: unknown): string | undefined =>
  isString(value) ? value : undefined;

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter(isString) : [];

const extractClinicalExplanation = (value: unknown): EpiClinicalExplanation | undefined => {
  if (!isRecord(value)) return undefined;
  const fields: EpiClinicalExplanation = {
    answers: asOptionalString(value['answers']),
    value: asOptionalString(value['value']),
    qualifyingCriteria: asOptionalString(value['qualifying_criteria']),
    timeWindow: asOptionalString(value['time_window']),
    valueSelection: asOptionalString(value['value_selection']),
    missingValues: asOptionalString(value['missing_values']),
    outOfScope: asOptionalString(value['out_of_scope']),
  };
  // All sections optional; return ``undefined`` if literally nothing
  // came through so the Description tab can fall back to the plain
  // ``description`` string instead of rendering an empty shell.
  return Object.values(fields).some((v) => v !== undefined) ? fields : undefined;
};

const conceptBindingStatuses = new Set(['selected', 'proposed', 'inline']);

const extractConceptBindings = (value: unknown): EpiConceptBinding[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const bindings: EpiConceptBinding[] = [];
  for (const entry of value) {
    if (!isRecord(entry)) continue;
    const conceptGroupId = asOptionalString(entry['concept_group_id']);
    const conceptGroupName = asOptionalString(entry['concept_group_name']);
    if (!conceptGroupId || !conceptGroupName) continue;
    const rawStatus = entry['status'];
    // Legacy 'curated' (the prior canonical value) normalises to 'selected';
    // unknown/missing also defaults to 'selected' (the most common path).
    const normalizedStatus = rawStatus === 'curated' ? 'selected' : rawStatus;
    const status =
      isString(normalizedStatus) && conceptBindingStatuses.has(normalizedStatus)
        ? (normalizedStatus as 'selected' | 'proposed' | 'inline')
        : 'selected';
    bindings.push({
      conceptGroupId,
      conceptGroupName,
      status,
      rationale: asOptionalString(entry['rationale']),
      alternativesConsidered: asStringArray(entry['alternatives_considered']),
    });
  }
  return bindings.length > 0 ? bindings : undefined;
};

/**
 * Structured term→concept mappings (OR-6214). Delegates the snake→camel
 * wire parse to the shared ``parseConceptMappings`` (feature-epi-chat) so the
 * Definition tab and the intent checkpoint card stay in lockstep; empty →
 * ``undefined`` so the section hides rather than rendering an empty shell.
 */
const extractConceptMappings = (value: unknown): ConceptMapping[] | undefined => {
  const mappings = parseConceptMappings(value);
  return mappings.length > 0 ? mappings : undefined;
};

const extractDesignDecisions = (value: unknown): EpiDesignDecision[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const decisions: EpiDesignDecision[] = [];
  for (const entry of value) {
    if (!isRecord(entry)) continue;
    const decision = asOptionalString(entry['decision']);
    const rationale = asOptionalString(entry['rationale']);
    if (!decision || !rationale) continue;
    decisions.push({ decision, rationale });
  }
  return decisions.length > 0 ? decisions : undefined;
};

const extractKnowledgeApplied = (value: unknown): EpiAppliedKnowledge[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const applied: EpiAppliedKnowledge[] = [];
  for (const entry of value) {
    if (!isRecord(entry)) continue;
    const id = asOptionalString(entry['id']);
    const kind = asOptionalString(entry['kind']);
    const scope = asOptionalString(entry['scope']);
    const source = asOptionalString(entry['source']);
    const title = asOptionalString(entry['title']);
    if (!id || !kind || !scope || !source || !title) continue;
    applied.push({
      id,
      kind,
      scope,
      source,
      title,
      granularity: asOptionalString(entry['granularity']),
    });
  }
  return applied.length > 0 ? applied : undefined;
};

const asNumber = (value: unknown): number | undefined =>
  typeof value === 'number' ? value : undefined;

const asRequiredNumber = (value: unknown): number | null =>
  typeof value === 'number' ? value : null;

const extractCoverage = (value: unknown): EpiExecutionCoverage | undefined => {
  if (!isRecord(value)) return undefined;
  const patientsInCohort = asNumber(value['patients_in_cohort']);
  const patientsWithValue = asNumber(value['patients_with_value']);
  const patientsZeroFilled = asNumber(value['patients_zero_filled']);
  if (patientsInCohort == null || patientsWithValue == null || patientsZeroFilled == null) {
    return undefined;
  }
  return {
    patientsInCohort,
    patientsWithValue,
    patientsZeroFilled,
    patientsMissingAnchor: asNumber(value['patients_missing_anchor']),
    patientsWithAnchor: asNumber(value['patients_with_anchor']),
    anchorSlug: asOptionalString(value['anchor_slug']),
  };
};

const extractDistribution = (value: unknown): EpiExecutionDistribution | undefined => {
  if (!isRecord(value)) return undefined;
  const kind = asOptionalString(value['kind']);
  if (kind === 'boolean') {
    const truePatients = asRequiredNumber(value['true_patients']);
    const falsePatients = asRequiredNumber(value['false_patients']);
    if (truePatients == null || falsePatients == null) return undefined;
    return {
      kind: 'boolean',
      truePatients,
      falsePatients,
      nullPatients: asNumber(value['null_patients']),
    };
  }
  if (kind === 'numeric') {
    const min = asRequiredNumber(value['min']);
    const p25 = asRequiredNumber(value['p25']);
    const median = asRequiredNumber(value['median']);
    const p75 = asRequiredNumber(value['p75']);
    const max = asRequiredNumber(value['max']);
    const mean = asRequiredNumber(value['mean']);
    if (
      min == null ||
      p25 == null ||
      median == null ||
      p75 == null ||
      max == null ||
      mean == null
    ) {
      return undefined;
    }
    const rawBuckets = value['numeric_buckets'];
    const buckets = Array.isArray(rawBuckets)
      ? rawBuckets.filter(isRecord).map((b) => ({
          lower: asNumber(b['lower']) ?? 0,
          upper: asNumber(b['upper']) ?? 0,
          count: asNumber(b['count']) ?? 0,
        }))
      : undefined;
    return {
      kind: 'numeric',
      min,
      p25,
      median,
      p75,
      max,
      mean,
      stdDev: asNumber(value['std_dev']),
      buckets,
    };
  }
  if (kind === 'date') {
    const earliest = asOptionalString(value['earliest']);
    const latest = asOptionalString(value['latest']);
    if (!earliest || !latest) return undefined;
    const rawBuckets = value['date_buckets'];
    const buckets = Array.isArray(rawBuckets)
      ? rawBuckets
          .filter(isRecord)
          .map((b) => ({ year: asNumber(b['year']) ?? 0, count: asNumber(b['count']) ?? 0 }))
      : undefined;
    return {
      kind: 'date',
      earliest,
      latest,
      medianDate: asOptionalString(value['median_date']),
      buckets,
    };
  }
  if (kind === 'string') {
    const rawTop = value['top_values'];
    const topValues = Array.isArray(rawTop)
      ? rawTop.filter(isRecord).map((t) => ({
          value: asOptionalString(t['value']) ?? '',
          count: asNumber(t['count']) ?? 0,
          percentage: asNumber(t['percentage']) ?? 0,
        }))
      : [];
    return {
      kind: 'string',
      topValues,
      otherCount: asNumber(value['other_count']),
      otherValueCount: asNumber(value['other_value_count']),
      otherPercentage: asNumber(value['other_percentage']),
    };
  }
  return undefined;
};

const extractConceptUsage = (value: unknown): EpiExecutionConceptUsage[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const usage: EpiExecutionConceptUsage[] = [];
  for (const entry of value) {
    if (!isRecord(entry)) continue;
    const conceptGroupId = asOptionalString(entry['concept_group_id']);
    const conceptGroupName = asOptionalString(entry['concept_group_name']);
    const boundConcepts = asNumber(entry['bound_concepts']);
    if (!conceptGroupId || !conceptGroupName || boundConcepts == null) continue;
    const rawTop = entry['top_concepts'];
    const topConcepts = Array.isArray(rawTop)
      ? rawTop.filter(isRecord).map((c) => ({
          conceptId: asOptionalString(c['concept_id']) ?? '',
          conceptName: asOptionalString(c['concept_name']) ?? '',
          eventCount: asNumber(c['event_count']) ?? 0,
        }))
      : undefined;
    usage.push({
      conceptGroupId,
      conceptGroupName,
      boundConcepts,
      usedConcepts: asNumber(entry['used_concepts']) ?? 0,
      topConcepts,
    });
  }
  return usage.length > 0 ? usage : undefined;
};

const extractTechnicalChecks = (value: unknown): EpiExecutionTechnicalChecks | undefined => {
  if (!isRecord(value)) return undefined;
  const sqlParsed = typeof value['sql_parsed'] === 'boolean' ? value['sql_parsed'] : false;
  const rawFindings = value['lint_findings'];
  const lintFindings = Array.isArray(rawFindings)
    ? rawFindings.filter(isRecord).map((f) => ({
        rule: asOptionalString(f['rule']) ?? '',
        severity: (f['severity'] === 'error' ? 'error' : 'warning') as 'error' | 'warning',
        message: asOptionalString(f['message']) ?? '',
        epiCopy: asOptionalString(f['epi_copy']),
      }))
    : [];
  return {
    sqlParsed,
    estimatedRuntimeSeconds: asNumber(value['estimated_runtime_seconds']),
    estimatedCostCredits: asNumber(value['estimated_cost_credits']),
    lintFindings,
  };
};

const extractAttrition = (value: unknown): EpiAttritionStep[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const steps: EpiAttritionStep[] = [];
  for (const entry of value) {
    if (!isRecord(entry)) continue;
    const name = asOptionalString(entry['name']);
    const description = asOptionalString(entry['description']);
    const subjects = asNumber(entry['subjects']);
    if (!name || description == null || subjects == null) continue;
    steps.push({ name, description, subjects });
  }
  return steps.length > 0 ? steps : undefined;
};

const EXECUTION_STATUSES = new Set(['pending', 'passed', 'failed', 'error', 'empty']);

const toExecutionStatus = (raw: unknown): EpiArtifactExecution['status'] => {
  const s = typeof raw === 'string' ? raw : '';
  return EXECUTION_STATUSES.has(s) ? (s as EpiArtifactExecution['status']) : 'passed';
};

const extractVerificationResults = (value: unknown): EpiArtifactExecution[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const executions: EpiArtifactExecution[] = [];
  for (const entry of value) {
    if (!isRecord(entry)) continue;
    const resultHash = asOptionalString(entry['result_hash']);
    const datasetReleaseId = asOptionalString(entry['dataset_release_id']);
    const computedAt = asOptionalString(entry['computed_at']);
    if (!resultHash || !datasetReleaseId || !computedAt) continue;
    executions.push({
      executionId: resultHash,
      versionId: asOptionalString(entry['version_id']),
      datasetReleaseId,
      datasetReleaseDisplayName: asOptionalString(entry['dataset_release_display_name']),
      resultHash,
      startedAt: computedAt,
      completedAt: computedAt,
      status: toExecutionStatus(entry['status']),
      studyStart: asOptionalString(entry['study_start']),
      studyEnd: asOptionalString(entry['study_end']),
      indexAvSlug: asOptionalString(entry['index_av_slug']),
      indexAvId: asOptionalString(entry['index_av_id']),
      indexCalendarDate: asOptionalString(entry['index_calendar_date']),
      logs: '',
      coverage: extractCoverage(entry['coverage']),
      distribution: extractDistribution(entry['distribution']),
      conceptUsage: extractConceptUsage(entry['concept_usage']),
      technicalChecks: extractTechnicalChecks(entry['technical_checks']),
      attrition: extractAttrition(entry['attrition']),
    });
  }
  return executions.length > 0 ? executions : undefined;
};
